import { useCallback, useMemo, useState } from 'react';

import { toast } from '@feathr/components';
import { useUser } from '@feathr/extender/state';
import { validate } from '@feathr/rachis';

import { useTimeFrameSelect } from './useTimeFrameSelect';

export interface IDateRangeOption {
  id: string;
  name: string;
  value: [string | undefined, string | undefined];
}

export interface IExportModalValidationErrors {
  email?: string[];
  dateRange?: string[];
}

interface IResolvedDateRange {
  startDate: string | undefined;
  endDate: string | undefined;
}

interface IUseExportModalReturn {
  email: string;
  emailHadFocus: boolean;
  customDateRange: [string | undefined, string | undefined];
  selectedDateRange: string;
  isLoading: boolean;
  isSubmitDisabled: boolean;
  validationErrors: IExportModalValidationErrors | undefined;
  dateRangeOptions: IDateRangeOption[];
  handleEmailBlur: () => void;
  handleChangeEmail: (newEmail?: string) => void;
  handleDateRangeChange: (option: IDateRangeOption) => void;
  handleCustomDateRangeChange: (value: [string | undefined, string | undefined]) => void;
  handleExport: (
    exportFn: () => Promise<unknown>,
    onSuccess: () => void,
    t: (key: string, options?: Record<string, unknown>) => string,
  ) => Promise<void>;
  setIsLoading: (loading: boolean) => void;
  resetFormState: () => void;
  resolveDateRange: () => IResolvedDateRange;
}

/**
 * Shared hook for export modal functionality.
 * Handles email validation, date range selection, and common modal state.
 *
 * @param t - Translation function from useTranslation
 * @returns Export modal state and handlers
 *
 * @example
 * ```tsx
 * const {
 *   email,
 *   validationErrors,
 *   handleChangeEmail,
 *   resolveDateRange,
 *   resetFormState,
 * } = useExportModal(t);
 * ```
 */
export function useExportModal(t: (key: string) => string): IUseExportModalReturn {
  const user = useUser();
  const { options: timeFrameOptions } = useTimeFrameSelect();

  const [email, setEmail] = useState<string>(user.email);
  const [emailHadFocus, setEmailHadFocus] = useState<boolean>(false);
  const [customDateRange, setCustomDateRange] = useState<[string | undefined, string | undefined]>([
    undefined,
    undefined,
  ]);
  const [selectedDateRange, setSelectedDateRange] = useState<string>('all_time');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const dateRangeOptions: IDateRangeOption[] = [
    { id: 'all_time', name: t('All time'), value: [undefined, undefined] },
    ...timeFrameOptions.map((opt) => ({
      id: opt.id,
      name: opt.name,
      value: opt.value as [string | undefined, string | undefined],
    })),
    { id: 'custom', name: t('Custom'), value: [undefined, undefined] },
  ];

  const handleEmailBlur = useCallback((): void => {
    setEmailHadFocus(true);
  }, []);

  const handleChangeEmail = useCallback((newEmail?: string): void => {
    setEmail(newEmail ?? '');
    setEmailHadFocus(true);
  }, []);

  const handleDateRangeChange = useCallback((option: IDateRangeOption): void => {
    setSelectedDateRange(option.id);
  }, []);

  const handleCustomDateRangeChange = useCallback(
    (value: [string | undefined, string | undefined]): void => {
      setCustomDateRange(value);
    },
    [],
  );

  const resetFormState = useCallback((): void => {
    setEmail(user.email);
    setEmailHadFocus(false);
    setCustomDateRange([undefined, undefined]);
    setSelectedDateRange('all_time');
  }, [user.email]);

  const resolveDateRange = useCallback((): IResolvedDateRange => {
    if (selectedDateRange === 'all_time') {
      return { startDate: undefined, endDate: undefined };
    }

    if (selectedDateRange === 'custom') {
      return { startDate: customDateRange[0], endDate: customDateRange[1] };
    }

    const selectedOption = timeFrameOptions.find((opt) => opt.id === selectedDateRange);
    if (selectedOption?.value && Array.isArray(selectedOption.value)) {
      return { startDate: selectedOption.value[0], endDate: selectedOption.value[1] };
    }

    return { startDate: undefined, endDate: undefined };
  }, [selectedDateRange, customDateRange, timeFrameOptions]);

  const validateForm = useCallback((): IExportModalValidationErrors | undefined => {
    const constraints: Record<string, unknown> = {
      email: {
        presence: {
          allowEmpty: false,
          message: '^Email address cannot be empty.',
        },
        email: {
          message: '^Email address is invalid.',
        },
      },
    };

    if (selectedDateRange === 'custom') {
      const startDate = customDateRange[0];
      const endDate = customDateRange[1];
      const today = new Date().toISOString().split('T')[0];

      if (!startDate || !endDate) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Both start and end dates are required for custom range.'],
        };
      }

      if (startDate > endDate) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Start date must be before end date.'],
        };
      }

      if (startDate > today || endDate > today) {
        return {
          email: validate({ email }, { email: constraints.email })?.email,
          dateRange: ['^Dates cannot be in the future.'],
        };
      }
    }

    return validate({ email }, { email: constraints.email });
  }, [email, selectedDateRange, customDateRange]);

  const validationErrors = validateForm();

  const isSubmitDisabled = useMemo(
    () =>
      isLoading ||
      validationErrors?.email !== undefined ||
      validationErrors?.dateRange !== undefined,
    [isLoading, validationErrors],
  );

  const handleExport = useCallback(
    async (
      exportFn: () => Promise<unknown>,
      onSuccess: () => void,
      t: (key: string, options?: Record<string, unknown>) => string,
    ): Promise<void> => {
      try {
        setIsLoading(true);
        await exportFn();
        toast(t('Started export. Check your email.'), {
          type: 'success',
        });
        onSuccess();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        toast(t("Couldn't export data. {{error}}", { error: errorMessage }), {
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    email,
    emailHadFocus,
    customDateRange,
    selectedDateRange,
    isLoading,
    isSubmitDisabled,
    validationErrors,
    dateRangeOptions,
    handleEmailBlur,
    handleChangeEmail,
    handleDateRangeChange,
    handleCustomDateRangeChange,
    handleExport,
    setIsLoading,
    resetFormState,
    resolveDateRange,
  };
}
